import type { LeaveRequest, LeaveRequestEvent } from "@spork/s4p-server-sdk";

/** School admin set the request back to pending. Not yet in the published s4p SDK union. */
export const LEAVE_REQUEST_STATUS_CHANGED_EVENT = "status_changed";

export function getLeaveRequestEvents(request: LeaveRequest): LeaveRequestEvent[] {
  return request.events ?? [];
}

export function isLeaveRequestStatusChangedEvent(type: string): boolean {
  return type === LEAVE_REQUEST_STATUS_CHANGED_EVENT;
}

export function getLeaveRequestEventDotClassName(type: string): string {
  switch (type) {
    case "approved":
    case "auto_approved":
    case "conditionally_approved":
      return "bg-success";
    case "rejected":
      return "bg-error";
    case "info_requested":
      return "bg-warning";
    case "cancelled":
      return "bg-muted-foreground";
    case "resubmitted":
    case "created":
    case "end_date_set":
    case LEAVE_REQUEST_STATUS_CHANGED_EVENT:
      return "bg-primary";
    case "attachment_added":
    case "attachment_removed":
      return "bg-border";
    default:
      return "bg-border";
  }
}

/** Localized "from → to" for a school-admin status override back to pending. */
export function getLeaveRequestStatusChangeDetail(
  event: Pick<LeaveRequestEvent, "payload">,
  translateStatus: (status: string) => string,
): string | undefined {
  const fromStatus = event.payload?.from_status?.trim();
  const toStatus = event.payload?.to_status?.trim();

  if (!fromStatus || !toStatus) {
    return undefined;
  }

  return `${translateStatus(fromStatus)} → ${translateStatus(toStatus)}`;
}

/** Index of the latest `info_requested` event, or -1 when missing. */
function getLatestInfoRequestedEventIndex(request: LeaveRequest): number {
  const events = getLeaveRequestEvents(request);

  for (let index = events.length - 1; index >= 0; index -= 1) {
    if (events[index]?.type === "info_requested") {
      return index;
    }
  }

  return -1;
}

/** Latest note from the school when additional information was requested. */
export function getLatestInfoRequestNote(request: LeaveRequest): string | null {
  const events = getLeaveRequestEvents(request);
  const index = getLatestInfoRequestedEventIndex(request);

  if (index < 0) {
    return null;
  }

  return events[index]?.payload?.note?.trim() || null;
}

/** True when a parent uploaded an attachment after the latest school info request. */
export function hasAttachmentAddedSinceInfoRequest(request: LeaveRequest): boolean {
  const events = getLeaveRequestEvents(request);
  const infoRequestedIndex = getLatestInfoRequestedEventIndex(request);

  if (infoRequestedIndex < 0) {
    return false;
  }

  return events
    .slice(infoRequestedIndex + 1)
    .some((event) => event.type === "attachment_added");
}

/**
 * Resubmit is allowed only after the parent added a new note and/or an attachment
 * since the school asked for more information.
 */
export function canSubmitLeaveRequestResubmit(
  request: LeaveRequest,
  message: string,
): boolean {
  return message.trim().length > 0 || hasAttachmentAddedSinceInfoRequest(request);
}
